/* eslint-disable @repo/internal/react/require-jsdoc */
import React, { type ReactNode } from 'react';

import { MDXProvider, type MDXProviderComponentsProp } from '@mdx-js/react';
import { Redirect, useLocation } from '@reach/router';

import {
	Banner,
	HeadingContextProvider,
	PageContentContainer,
	Paragraph,
	SectionHeadingLink,
	ThemeableImage,
} from '@af/design-system-docs-ui';
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import { Show } from '@atlaskit/primitives/responsive';
import SectionMessage from '@atlaskit/section-message';
import Tabs from '@atlaskit/tabs';
import { ContextualAnalyticsData, FireScreenAnalytics, SCREEN } from '@atlassian/analytics-bridge';

import FeedbackSection from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/feedback-analytics/feedback-section';
import LocalNav from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/local-nav/local-nav';
import MetaTags from '../__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/components/meta-tags';
import { Tab, TabList, TabPanel } from '../components/tabs';
import { normalizeSourceName } from '../utils/analytics';
import titleify from '../utils/titleify';

type L2PageProps = {
	title: string;
	description: string;
	image?: {
		light: string;
		dark: string;
	};
	categorySlug: string;
	entries: Array<any>;
	tileColor?: string;
	defaultIcon?: string;
	guidelineCategory?: string;
	children?: ReactNode;
	tabs?: Array<{
		label: string;
		content: any;
		url: string;
	}>;
};

const L2Page = ({
	title,
	description,
	image,
	categorySlug,
	entries,
	children,
	tabs,
}: L2PageProps) => {
	const location = useLocation();
	const selectedTab = tabs?.findIndex((t) => location.pathname.includes(t.url));

	if (tabs && selectedTab && selectedTab < 0) {
		return <Redirect to={tabs[0].url} noThrow />;
	}

	const tabContent = tabs && (
		<Tabs selected={selectedTab} shouldUnmountTabPanelOnChange id="default">
			<TabList>
				{tabs.map((tabData) => {
					return <Tab key={tabData.label} name={tabData.label} to={tabData.url} />;
				})}
			</TabList>
			{tabs.map((tabData) => {
				return (
					<TabPanel key={tabData.label}>
						<Box paddingBlockEnd="space.400">
							<Stack space="space.800">
								<section>{tabData.content}</section>
							</Stack>
						</Box>
					</TabPanel>
				);
			})}
		</Tabs>
	);
	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="index"
			attributes={{
				name: `${normalizeSourceName(title)}Home`,
				entriesCount: entries.length,
			}}
		>
			<MetaTags title={title} description={description} categorySlug={titleify(categorySlug)} />
			<FireScreenAnalytics key={title} />
			<Banner title={title} description={description} image={image} hasBackgroundColor />
			<HeadingContextProvider>
				<MDXProvider components={mdxComponents}>
					<Flex justifyContent="center">
						<PageContentContainer>
							<Box paddingBlockEnd="space.400">
								<Inline space="space.500" alignBlock="stretch">
									{tabs ? (
										<Box xcss={[tabContainerStyles]}>{tabContent}</Box>
									) : (
										<Box paddingBlock="space.400">{children}</Box>
									)}
									<Show above="sm">
										<LocalNav />
									</Show>
								</Inline>
								<FeedbackSection
									tabName={tabs ? tabs[selectedTab!].label : undefined}
									title={title}
									pageUrl={location.href}
								/>
							</Box>
						</PageContentContainer>
					</Flex>
				</MDXProvider>
			</HeadingContextProvider>
		</ContextualAnalyticsData>
	);
};

export default L2Page;

const tabContainerStyles = xcss({
	marginBlockStart: 'space.negative.400',
});

const ulStyles = xcss({
	marginBlock: 'space.200',
});

const sectionMessageStyles = xcss({
	marginBlockEnd: 'space.200',
});

const mdxComponents: MDXProviderComponentsProp = {
	h2: ({ id, children }) => (
		<SectionHeadingLink id={id} depth={2} isBrandHeading>
			{children}
		</SectionHeadingLink>
	),
	h3: ({ id, children }) => (
		<SectionHeadingLink id={id} depth={3} isBrandHeading>
			{children}
		</SectionHeadingLink>
	),
	h4: ({ id, children }) => (
		<SectionHeadingLink id={id} depth={4} isBrandHeading>
			{children}
		</SectionHeadingLink>
	),
	p: ({ children }) => <Paragraph size="medium">{children}</Paragraph>,
	img: ({ src, srcDark, alt, borderAppearance, caption }) => (
		<ThemeableImage
			src={src}
			srcDark={srcDark}
			alt={alt}
			borderAppearance={borderAppearance}
			caption={caption}
		/>
	),
	ul: ({ children }) => (
		<Box as="ul" xcss={ulStyles}>
			{children}
		</Box>
	),
	// Temporary solution to add bottom margin on section messages
	// TODO: Revist page spacing as a whole once Contentful is gone
	SectionMessage: ({ title, children }) => (
		<Box xcss={sectionMessageStyles}>
			<SectionMessage title={title}>{children}</SectionMessage>
		</Box>
	),
};
